import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';

const InformationPageDetail = () => {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

    useEffect(() => {
        const fetchPageDetail = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE_URL}/web-pages/information/slug/${slug}`);
                if (response.data.success) {
                    setPage(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching information page details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchPageDetail();
        }
        window.scrollTo(0, 0);
    }, [slug]);

    const layoutStyle = { minHeight: '60vh', paddingTop: '120px' };

    if (loading) {
        return (
            <div className="main-wrapper">
                <Header />
                <div className="text-center" style={layoutStyle}>
                    <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!page) {
        return (
            <div className="main-wrapper">
                <Header />
                <div className="container text-center" style={layoutStyle}>
                    <h2>Page not found.</h2>
                    <p className="text-muted mt-3 mb-4">The web page you are looking for does not exist or has been removed.</p>
                    <Link to="/" className="btn btn-danger">Back to Home</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]+>/g, '').substring(0, 160) + '...';
    };

    const formattedLastUpdatedDate = page.last_updated_date
        ? new Date(`${String(page.last_updated_date).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : '';
    const pageDescription = String(page.description || '').replace(
        /<(p|div)[^>]*>\s*(?:<[^>]+>\s*)*last\s+updated\s*:[\s\S]*?<\/\1>/gi,
        ''
    );
    const lastUpdatedMarkup = formattedLastUpdatedDate
        ? `<p class="information-page-last-updated text-muted">Last Updated: ${formattedLastUpdatedDate}</p>`
        : '';
    const descriptionWithLastUpdatedDate = lastUpdatedMarkup
        ? pageDescription.replace(
            /(<h([1-6])\b[^>]*>[\s\S]*?<\/h\2>)/i,
            `$1${lastUpdatedMarkup}`
        )
        : pageDescription;
    const renderedPageDescription = lastUpdatedMarkup && descriptionWithLastUpdatedDate === pageDescription
        ? `${lastUpdatedMarkup}${pageDescription}`
        : descriptionWithLastUpdatedDate;

    // Style elements for dynamic contents
    const bannerStyle = {
        background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.65)), url(${page.image_url || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600&q=80'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '100px 0 80px 0',
        color: '#ffffff',
        borderRadius: '0 0 20px 20px',
        marginBottom: '40px'
    };

    return (
        <div className="main-wrapper" style={{ background: '#fcfcfc' }}>
            <SEO 
                title={page.meta_title || `${page.page_title} - Mangoloops`} 
                description={page.meta_desc || stripHtml(page.description)} 
                keywords={page.meta_keywords}
                ogTitle={page.meta_title || `${page.page_title} - Mangoloops`}
                ogDescription={page.meta_desc || stripHtml(page.description)}
            />
            <Header />
            
            <div style={{ paddingTop: '100px', minHeight: '65vh' }}>
                {/* Premium Hero Banner */}
                <div style={bannerStyle}>
                    <div className="container">
                        <div className="row justify-content-center text-center">
                            <div className="col-lg-8">
                                <h1 className="display-4 font-weight-bold mb-3" style={{ fontSize: '2.8rem', letterSpacing: '-0.5px' }}>
                                    {page.page_title}
                                </h1>
                                <p style={{ fontSize: '1.05rem', opacity: 0.85 }}>
                                    Home <i className="bi bi-chevron-right mx-2 fs-12"></i> {page.page_title}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="container pb-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-9">
                            <div 
                                className="card border-0 shadow-sm p-4 p-md-5" 
                                style={{ 
                                    borderRadius: '16px', 
                                    background: '#ffffff',
                                    lineHeight: '1.8',
                                    color: '#333333',
                                    fontSize: '1.05rem'
                                }}
                            >
                                <div 
                                    className="page-dynamic-html-content"
                                    dangerouslySetInnerHTML={{ __html: renderedPageDescription }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Footer />

            <style>{`
                .page-dynamic-html-content h2 {
                    font-weight: 700;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                    color: #1e1e1e;
                    border-bottom: 2px solid #f1f3f5;
                    padding-bottom: 0.5rem;
                }
                .page-dynamic-html-content .information-page-last-updated {
                    margin: 0.75rem 0 1.5rem;
                    text-align: center;
                }
                .page-dynamic-html-content h3 {
                    font-weight: 600;
                    margin-top: 1.5rem;
                    margin-bottom: 0.8rem;
                    color: #2b2b2b;
                }
                .page-dynamic-html-content p {
                    margin-bottom: 1.25rem;
                    color: #4a4a4a;
                }
                .page-dynamic-html-content .lead {
                    font-size: 1.2rem;
                    color: #5a5a5a;
                    font-weight: 400;
                }
                .page-dynamic-html-content ul {
                    margin-bottom: 1.5rem;
                    padding-left: 1.5rem;
                }
                .page-dynamic-html-content li {
                    margin-bottom: 0.5rem;
                    color: #4a4a4a;
                }
                .page-dynamic-html-content a {
                    color: #dc3545;
                    text-decoration: none;
                }
                .page-dynamic-html-content a:hover {
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
};

export default InformationPageDetail;
